import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useOutletContext, useSearchParams, useNavigate } from 'react-router-dom';
import { useTestingProjects } from './hooks';
import { UTProjectRow, UTDeleteModal } from './components';
import GlassButton from '../../components/Aero/GlassButton';
import AeroPageHeader from '../../components/Aero/AeroPageHeader';
import AeroIcon from '../../components/Aero/AeroIcon';
import AeroDotLoader from '../../components/Aero/AeroDotLoader';
import { AeroEmptyPanel } from '../../components/Aero/AeroHomeParts';
import AeroStatePanel, { stateFromError } from '../../components/Aero/AeroStatePanel';
import '../AeroSites/AeroSites.css';
import '../AeroHome/AeroHome.css';
import DraftCard from '../../components/shared/DraftCard';
import { discardDraft } from '../../utils/drafts';
import './UserTestingDashboard.css';
import './UserTestingV3.css';
import '../../styles/userTesting/index.css';
import '../../styles/aero-ut-theme.css';

/**
 * Derive a display status for a project based on its data.
 * An explicit stored status of 'completed' is always respected.
 * Auto-derives 'completed' when a positive targetSessions goal has been
 * met by the session count (read-time only; nothing is written to Firestore).
 */
function deriveStatus(project) {
  if (project.userTesting?.status === 'completed') return 'completed';
  const target = Number(project.userTesting?.targetSessions) || 0;
  if (target > 0 && (project.sessionCount || 0) >= target) return 'completed';
  if (project.userTesting?.status) return project.userTesting.status;
  if ((project.sessionCount || 0) === 0) return 'draft';
  return 'active';
}

function UserTestingDashboard() {
  const { user } = useOutletContext();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');

  const { projects, loading, error, refetch, deleteProject, renameProject, duplicateProject } = useTestingProjects(user?.uid);



  // New test is a page (/user-testing/new, v3) with the same CreateFlow.
  const handleNewTest = useCallback(() => navigate('/user-testing/new'), [navigate]);

  const [discardTarget, setDiscardTarget] = useState(null);
  const [discarding, setDiscarding] = useState(false);

  // Creation-drafts (in-progress CreateFlow tests) are surfaced separately
  // from live tests: they carry no sessions or stats, only Resume and Discard,
  // and are kept out of the stats, filter counts and the live grid below.
  const draftProjects = useMemo(() => projects.filter((p) => p.draft), [projects]);
  const liveProjects = useMemo(() => projects.filter((p) => !p.draft), [projects]);

  // Resume opens the same page on that draft, restored to the step it was
  // left at (from its draftMeta).
  const handleResumeDraft = useCallback((project) => {
    navigate(`/user-testing/new?draft=${encodeURIComponent(project.id)}`);
  }, [navigate]);

  const handleConfirmDiscard = useCallback(async () => {
    if (!user?.uid || !discardTarget || discarding) return;
    setDiscarding(true);
    try {
      await discardDraft({ uid: user.uid, urlId: discardTarget.id });
      setDiscardTarget(null);
      await refetch();
    } catch (err) {
      console.error('Error discarding draft:', err);
    } finally {
      setDiscarding(false);
    }
  }, [user, discardTarget, discarding, refetch]);

  useEffect(() => {
    document.title = 'Tests | Aero';
  }, []);

  const statusFilter = searchParams.get('filter');

  const statusFilteredProjects = useMemo(() => {
    if (!statusFilter) return liveProjects;
    return liveProjects.filter((p) => deriveStatus(p) === statusFilter);
  }, [liveProjects, statusFilter]);

  const filteredProjects = useMemo(() => {
    if (!searchQuery.trim()) return statusFilteredProjects;

    const query = searchQuery.toLowerCase();
    return statusFilteredProjects.filter((project) =>
      project.title.toLowerCase().includes(query) ||
      project.generatedUrl?.toLowerCase().includes(query) ||
      project.customDomain?.toLowerCase().includes(query)
    );
  }, [statusFilteredProjects, searchQuery]);

  const stats = useMemo(() => {
    const totalSessions = liveProjects.reduce((sum, p) => sum + p.sessionCount, 0);
    const avgCompletion = liveProjects.length > 0
      ? Math.round(liveProjects.reduce((sum, p) => sum + p.completionRate, 0) / liveProjects.length)
      : 0;

    const activeCount = liveProjects.filter((p) => deriveStatus(p) === 'active').length;
    const completedCount = liveProjects.filter((p) => deriveStatus(p) === 'completed').length;

    return {
      totalTests: liveProjects.length,
      activeCount,
      completedCount,
      totalSessions,
      avgCompletion
    };
  }, [liveProjects]);

  const filterCounts = useMemo(() => ({
    all: liveProjects.length,
    active: liveProjects.filter((p) => deriveStatus(p) === 'active').length,
    completed: liveProjects.filter((p) => deriveStatus(p) === 'completed').length,
    draft: liveProjects.filter((p) => deriveStatus(p) === 'draft').length,
  }), [liveProjects]);

  if (loading) {
    return (
      <div className="aero-main aero-tests-page">
        <AeroPageHeader crumbs="Tests" />
        <div className="aero-create-wait"><AeroDotLoader size="md" label="Loading" /></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="aero-main aero-tests-page">
        <AeroPageHeader crumbs="Tests" />
        {/* the raw error is logged by useTestingProjects; only its kind shows */}
        <AeroStatePanel
          kind={stateFromError({ message: error }) === 'offline' ? 'offline' : 'error'}
          title={stateFromError({ message: error }) === 'offline' ? undefined : "Your tests didn't load"}
          actions={<GlassButton fill="ink" size="sm" onClick={refetch}>Try again</GlassButton>}
        />
      </div>
    );
  }

  return (
    <div className="aero-main aero-tests-page">
      <AeroPageHeader
        crumbs="Tests"
        actions={(
          <>
            <GlassButton fill="glass" size="sm" onClick={refetch}>
              Refresh
            </GlassButton>
            <GlassButton fill="ink" size="sm" onClick={handleNewTest}>
              <AeroIcon name="plus" />
              New test
            </GlassButton>
          </>
        )}
      />

      <div className="aero-sites-body">
        {liveProjects.length > 0 && (
          <div className="aero-sites-filter">
            <div className="aero-seg" role="tablist" aria-label="Show">
              {[
                [null, 'All', filterCounts.all, '/user-testing'],
                ['active', 'Open', filterCounts.active, '/user-testing?filter=active'],
                ['completed', 'Closed', filterCounts.completed, '/user-testing?filter=completed'],
                ['draft', 'Draft', filterCounts.draft, '/user-testing?filter=draft'],
              ].map(([key, label, count, to]) => (
                <button
                  key={label}
                  type="button"
                  role="tab"
                  aria-selected={(statusFilter || null) === key}
                  className={`aero-seg-item${(statusFilter || null) === key ? ' on' : ''}`}
                  onClick={() => navigate(to)}
                >
                  {label}
                  <span className="aero-seg-ct">{count}</span>
                </button>
              ))}
            </div>
            <label className="aero-search aero-sites-search">
              <AeroIcon name="search" />
              <input
                type="text"
                placeholder="Search tests"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </label>
          </div>
        )}

        {liveProjects.length > 0 && (
          <div className="aero-tests-stats">
            <span><b>{stats.totalTests}</b> tests</span>
            <span><b>{stats.activeCount}</b> open</span>
            <span><b>{stats.totalSessions}</b> sessions</span>
            <span><b>{stats.avgCompletion}%</b> of tasks done</span>
          </div>
        )}

        {projects.length === 0 ? (
          <AeroEmptyPanel
            wash="test"
            title="Watch someone use it"
            lead="Make a test from a Figma prototype, an HTML file or one of your sites. You get one link to share, and each session shows up here when it ends."
          >
            <GlassButton fill="ink" size="md" onClick={handleNewTest}>
              <AeroIcon name="plus" />
              New test
            </GlassButton>
          </AeroEmptyPanel>
        ) : (
          <>
            {draftProjects.length > 0 && (
              <section className="aero-tests-drafts">
                <div className="aero-home-hh"><h2>Unfinished</h2><span className="aero-home-ct">{draftProjects.length}</span></div>
                <div className="aero-test-grid">
                  {draftProjects.map((project) => (
                    <DraftCard
                      key={project.id}
                      title={project.title}
                      subtitle="Setup not finished"
                      updatedLabel={
                        (project.updatedAt || project.createdAt)
                          ? new Date(project.updatedAt || project.createdAt).toLocaleDateString()
                          : 'just now'
                      }
                      onResume={() => handleResumeDraft(project)}
                      onDiscard={() => setDiscardTarget(project)}
                    />
                  ))}
                </div>
              </section>
            )}

            {filteredProjects.length === 0 ? (
              liveProjects.length > 0 && (
                <div className="aero-home-quiet">
                  {searchQuery ? `No tests match "${searchQuery}".` : `No ${statusFilter === 'active' ? 'open' : statusFilter === 'completed' ? 'closed' : (statusFilter || '')} tests.`}
                </div>
              )
            ) : (
              <div className="aero-tlist aero-tests-list">
                <div className="aero-tests-row aero-tests-row-head" aria-hidden="true">
                  <span />
                  <span>Test</span>
                  <span>Test link</span>
                  <span>Sessions</span>
                  <span>Activity</span>
                  <span>Status</span>
                  <span />
                </div>
                {filteredProjects.map((project) => (
                  <UTProjectRow
                    key={project.id}
                    project={project}
                    onDelete={deleteProject}
                    onRename={renameProject}
                    onDuplicate={duplicateProject}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      <UTDeleteModal
        isOpen={!!discardTarget}
        type="draft"
        onConfirm={handleConfirmDiscard}
        onCancel={() => !discarding && setDiscardTarget(null)}
        loading={discarding}
      />
    </div>
  );
}

export default UserTestingDashboard;
